import { Product, Category, Reservation } from '../types';
import axios, { AxiosError, AxiosResponse } from 'axios';

// Configuration de l'API avec PROXY
// Les URLs relatives /api seront proxifiées vers le backend par setupProxy.js
const API_URL = '/api';

// Créer une instance axios personnalisée avec intercepteurs
// @ts-ignore - axios.create existe mais TypeScript peut avoir des problèmes de typage avec certaines versions
const api = axios.create({
    baseURL: API_URL
});

// Exporter l'instance api pour pouvoir l'utiliser directement dans les composants
export default api;

// Configuration d'axios avec intercepteur pour ajouter le token JWT
api.interceptors.request.use(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config: any) => {
        // Récupérer le token depuis localStorage
        const token = localStorage.getItem('adminToken');

        // Ajouter le token à l'en-tête Authorization si disponible
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// Intercepteur de réponse pour gérer les erreurs d'authentification
api.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
        // Si le token est expiré ou invalide, rediriger vers la page d'accueil
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            console.error('Erreur d\'authentification:', error.response.status);
            // Supprimer le token invalide
            localStorage.removeItem('adminToken');
            // Vous pouvez ajouter une redirection ici si nécessaire
            // window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

export const fetchCategories = async (): Promise<Category[]> => {
    const response = await api.get('/categories');
    return response.data;
};

export const fetchProducts = async (): Promise<Product[]> => {
    const response = await api.get('/products');
    return response.data;
};

export const fetchProductsByCategory = async (slug: string) => {
    const response = await api.get(`/products/category/${slug}`);
    return response.data;
};

export const fetchProductById = async (id: string) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
};

// Fonctions pour les catégories
export const getCategories = async () => {
    const response = await api.get('/categories');
    return response.data;
};

export const createCategory = async (category: any) => {
    try {
        const response = await api.post('/categories', category);
        return response.data;
    } catch (error: unknown) {
        console.error('Erreur lors de la création de la catégorie');
        throw error;
    }
};

export const updateCategory = async (id: string, category: any) => {
    const response = await api.put(`/categories/${id}`, category);
    return response.data;
};

export const deleteCategory = async (id: string) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
};

export const uploadCategoryImage = async (categoryId: string, imageFile: File): Promise<any> => {
    const formData = new FormData();
    formData.append('image', imageFile);

    try {
        // D'abord, supprimer l'ancienne image
        await api.delete(`/categories/${categoryId}/image`);

        // Ensuite, uploader la nouvelle image
        const response = await api.put(
            `/categories/${categoryId}/image`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );

        return response.data;
    } catch (error: unknown) {
        console.error('Erreur lors de l\'upload de l\'image de catégorie');
        throw error;
    }
};

// Fonctions pour les produits
export const createProduct = async (product: any) => {
    try {
        const response = await api.post('/products', product);
        return response.data;
    } catch (error: unknown) {
        console.error('Erreur lors de la création du produit');
        throw error;
    }
};

export const updateProduct = async (id: string, product: any) => {
    const response = await api.put(`/products/${id}`, product);
    return response.data;
};

export const deleteProduct = async (id: string) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
};

export const uploadProductImage = async (productId: string, imageFile: File): Promise<string> => {
    try {
        // Créer FormData pour l'upload
        const formData = new FormData();
        formData.append('image', imageFile);

        // Essayer l'upload via l'API backend
        try {
            const response = await api.put(`/products/${productId}/image`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 30000
            });

            return response.data.imageUrl;

        } catch (apiError) {
            // Fallback: conversion en base64
            const base64Image = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(imageFile);
            });

            // Sauvegarder l'image base64 dans la base de données
            await api.put(`/products/${productId}`, {
                imageUrl: base64Image
            });

            return base64Image;
        }

    } catch (error: unknown) {
        console.error('Erreur lors de l\'upload de l\'image produit');
        throw error;
    }
};

export const fetchTimeSlots = async (productId: string, date: string): Promise<string[]> => {
    const response = await api.get(`/products/${productId}/time-slots`, {
        params: { date }
    });
    return response.data;
};

export const fetchReservations = async (): Promise<Reservation[]> => {
    const response = await api.get('/reservations');
    return response.data;
};

export const createReservation = async (data: Omit<Reservation, '_id'>): Promise<Reservation> => {
    const response = await api.post('/reservations', data);
    return response.data;
};

export const updateReservation = async (id: string, data: Partial<Reservation>): Promise<Reservation> => {
    const response = await api.put(`/reservations/${id}`, data);
    return response.data;
};

export const deleteReservation = async (id: string): Promise<void> => {
    await api.delete(`/reservations/${id}`);
};